import { useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: customer pages use generated API outside src/
import type { Id } from '../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: customer pages use generated dataModel outside src/
import { useBatchSelection } from '@/hooks/useBatchSelection';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DeleteConfirmDialog } from '@/components/insurance/DeleteConfirmDialog';
import {
  Plus,
  FileText,
  ArrowLeft,
  ClipboardList,
  Clock,
  CheckCircle2,
  FilePen,
  Trash2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { TPreAuthStatus, IMyClaimsStatsCardProps, IPreAuthorization } from '@/@types/claims';

function preAuthStatusVariant(
  status: TPreAuthStatus
): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'approved':
      return 'default';
    case 'under_review':
    case 'requested':
      return 'secondary';
    case 'denied':
      return 'destructive';
    case 'draft':
      return 'secondary';
    default:
      return 'outline';
  }
}

function preAuthStatusLabel(status: TPreAuthStatus): string {
  return status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

// NEW_FUNCTION_JUSTIFIED: StatsCard sub-component reused pattern from MyClaimsListPage — displays pre-auth count per category
function StatsCard({ icon: Icon, label, value, iconClass, valueClass }: IMyClaimsStatsCardProps) {
  return (
    <Card className="p-4">
      <div className="flex items-center gap-3">
        <Icon className={cn('h-5 w-5 text-muted-foreground', iconClass)} />
        <div>
          <p className={cn('text-2xl font-bold', valueClass)}>{value}</p>
          <p className="text-xs text-muted-foreground">{label}</p>
        </div>
      </div>
    </Card>
  );
}

export function MyPreAuthListPage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const basePath = pathname.startsWith('/company') ? '/company/my-insurance' : '/my-insurance';

  const preAuths = useQuery(api.insurance.preAuthorizations.listMy);
  const batchDelete = useMutation(api.insurance.preAuthorizations.batchDeleteDrafts);

  const draftPreAuths = (preAuths ?? []).filter((pa: IPreAuthorization) => pa.status === 'draft');

  const onDelete = useCallback(
    async (ids: string[]) => {
      await batchDelete({
        preAuthIds: ids as Id<'preAuthorizations'>[], // AS_JUSTIFIED: ids come from pa._id which are Id<'preAuthorizations'>
      });
    },
    [batchDelete]
  );

  const {
    selectedIds,
    isDeleteOpen,
    setIsDeleteOpen,
    handleSelectRow,
    handleSelectAll,
    handleDeleteConfirm,
    isAllSelected,
    isSomeSelected,
    selectedCount,
  } = useBatchSelection(draftPreAuths, onDelete);

  if (preAuths === undefined) {
    return (
      <div className="flex-1 p-8 space-y-4">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-20" />
          ))}
        </div>
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const totalPreAuths = preAuths.filter((pa: IPreAuthorization) => pa.status !== 'draft').length;
  const inReview = preAuths.filter(
    (pa: IPreAuthorization) => pa.status === 'requested' || pa.status === 'under_review'
  ).length;
  const approved = preAuths.filter((pa: IPreAuthorization) => pa.status === 'approved').length;
  const drafts = preAuths.filter((pa: IPreAuthorization) => pa.status === 'draft').length;

  return (
    <div className="flex-1 p-8">
      <Button variant="ghost" size="sm" className="mb-2" onClick={() => navigate(basePath)}>
        <ArrowLeft className="h-4 w-4 mr-2" /> Back
      </Button>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold">My Pre-Authorizations</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Request and track pre-authorization for treatments
          </p>
        </div>
        <div className="flex items-center gap-2">
          {selectedCount > 0 && (
            <Button variant="destructive" size="sm" onClick={() => setIsDeleteOpen(true)}>
              <Trash2 className="h-4 w-4 mr-1.5" />
              Delete ({selectedCount})
            </Button>
          )}
          <Button onClick={() => navigate(`${basePath}/pre-authorizations/new`)}>
            <Plus className="h-4 w-4 mr-2" />
            New Request
          </Button>
        </div>
      </div>

      {preAuths.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <FileText className="h-12 w-12 text-muted-foreground mb-4" />
          <p className="text-muted-foreground font-medium">No pre-authorization requests yet</p>
          <p className="text-sm text-muted-foreground mt-1">
            Submit a pre-authorization request before scheduled treatments
          </p>
          <Button className="mt-4" onClick={() => navigate(`${basePath}/pre-authorizations/new`)}>
            <Plus className="h-4 w-4 mr-2" />
            New Request
          </Button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-4 gap-4 mb-6">
            <StatsCard icon={ClipboardList} label="Total" value={totalPreAuths} />
            <StatsCard
              icon={Clock}
              label="In Review"
              value={inReview}
              iconClass="text-orange-500"
              valueClass="text-orange-500"
            />
            <StatsCard
              icon={CheckCircle2}
              label="Approved"
              value={approved}
              iconClass="text-green-600"
              valueClass="text-green-600"
            />
            <StatsCard icon={FilePen} label="Drafts" value={drafts} />
          </div>

          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    {draftPreAuths.length > 0 && (
                      <Checkbox
                        checked={isAllSelected ? true : isSomeSelected ? 'indeterminate' : false}
                        onCheckedChange={handleSelectAll}
                        aria-label="Select all draft pre-authorizations"
                      />
                    )}
                  </TableHead>
                  <TableHead>PA #</TableHead>
                  <TableHead>Treatment Type</TableHead>
                  <TableHead>Proposed Treatment</TableHead>
                  <TableHead>Estimated Cost</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {preAuths.map((pa: IPreAuthorization) => {
                  const isDraft = pa.status === 'draft';
                  const href = isDraft
                    ? `${basePath}/pre-authorizations/new?draftId=${pa._id}`
                    : `${basePath}/pre-authorizations/${pa._id}`;
                  return (
                    <TableRow
                      key={pa._id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => navigate(href)}
                    >
                      <TableCell onClick={e => e.stopPropagation()}>
                        {isDraft && (
                          <Checkbox
                            checked={selectedIds.has(pa._id)}
                            onCheckedChange={checked => handleSelectRow(pa._id, !!checked)}
                            aria-label={`Select pre-auth ${pa.preAuthNumber}`}
                          />
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-sm">{pa.preAuthNumber}</TableCell>
                      <TableCell className="capitalize">{pa.treatmentType ?? '—'}</TableCell>
                      <TableCell className="max-w-48 truncate">
                        {pa.proposedTreatment ?? '—'}
                      </TableCell>
                      <TableCell>
                        {pa.estimatedCost != null
                          ? `${pa.currency ?? ''} ${pa.estimatedCost.toLocaleString()}`
                          : '—'}
                      </TableCell>
                      <TableCell>
                        <Badge variant={preAuthStatusVariant(pa.status)}>
                          {preAuthStatusLabel(pa.status)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {new Date(pa.createdAt).toLocaleDateString()}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </>
      )}

      <DeleteConfirmDialog
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        onConfirm={handleDeleteConfirm}
        count={selectedCount}
        entityLabel="pre-authorization draft"
      />
    </div>
  );
}
